import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useAppSelector } from 'src/store';
import { useLoader } from 'src/provider/LoaderProvider';
import {
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useGetCustomTemplatesQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
} from 'src/services';
import moment from 'moment/moment';
import { Skeleton } from 'src/components/ui/skeleton';
import OnboardingStep4PrompBuilder from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep4/OnboardingStep4PrompBuilder/OnboardingStep4PrompBuilder';
import { OnboardingStep4Manual } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep4/OnboardingStep4Manual/OnboardingStep4Manual';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-60'} />
    </div>
  );
};

export const OnboardingStep4 = () => {
  const { promptType, project } = useAppSelector((s) => s.onboardingSlice);
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState(null);
  const [activeSettings, setActiveSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManual, setIsManual] = useState(null);
  const [inputData, setInputData] = useState(null);
  const [formFields, setFormFields] = useState(null);
  const [formFieldsObject, setFormFieldsObject] = useState({});
  const loader = useLoader();
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } =
    useGetDefaultConfigurationTemplateFieldsQuery({}, { refetchOnMountOrArgChange: true });
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      { id: project.id, page: settingsPage },
      { skip: !Boolean(project.id && defaultTemplateFields), refetchOnMountOrArgChange: true },
    );
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: project.id, settings_id: activeSettings && activeSettings.id },
      {
        skip: !Boolean(project.id && activeSettings && defaultTemplateFields),
        refetchOnMountOrArgChange: true,
      },
    );
  const { data: customTemplatesData, isLoading: isLoadingCustomTemplates } = (
    useGetCustomTemplatesQuery as any
  )();

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingCustomTemplates ||
      isManual === null ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingConfigurationSettingsPrompt,
    isLoadingCustomTemplates,
    isManual,
    settings,
  ]);

  useEffect(() => {
    if (defaultTemplateFields) {
      if (!activeSettings && settings?.length) {
        setActiveSettings(settings[0]);
      }

      if (activeSettings) {
        const newActiveSettings = settings.find((item) => {
          return item.id === activeSettings.id;
        });

        setActiveSettings(newActiveSettings);
      } else {
        const fields = (defaultTemplateFields as any).form_fields.map((field, index) => ({
          ...field,
          index,
        }));
        setFormFields(fields);
        setInputData(null);
      }
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (formFields) {
      const fieldsObject = formFields.reduce((prev, current) => {
        prev[current.key] = current;

        return prev;
      }, {});

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (configurationSettingsPrompt && !(configurationSettingsPrompt as any).is_manual) {
      const fields = (configurationSettingsPrompt as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));

      setFormFields(fields);
      setInputData((configurationSettingsPrompt as any).input_data);
    } else if (defaultTemplateFields) {
      const fields = (defaultTemplateFields as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));
      setFormFields(fields);
      setInputData(null);
    }
  }, [configurationSettingsPrompt, defaultTemplateFields]);

  useEffect(() => {
    if (allConfigurationSettings) {
      setSettings((prevState) => {
        const prev = prevState || [];
        const newState = [...prev];

        (allConfigurationSettings as any).items.forEach((item) => {
          const index = prev.findIndex((prevItem) => {
            return prevItem.id === item.id;
          });

          if (index > -1) {
            newState[index] = item;
          } else {
            newState.push(item);
          }
        });

        newState.sort((a, b) => +moment(b.created_at) - +moment(a.created_at));

        return newState;
      });

      if ((allConfigurationSettings as any).page_info.has_next_page) {
        setSettingsPage((allConfigurationSettings as any).page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (promptType) {
      setIsManual(promptType === 'manual');
    }
  }, [promptType]);

  return (
    <div className={'flex flex-col items-center w-full max-w-[550px]'}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <div className={clsx('flex flex-col w-full', !isManual && 'max-w-[455px]')}>
            {isManual ? (
              <OnboardingStep4Manual
                customTemplatesData={customTemplatesData}
                configurationSettingsPrompt={configurationSettingsPrompt}
              />
            ) : (
              <OnboardingStep4PrompBuilder
                activeSettings={activeSettings}
                inputData={inputData}
                formFieldsObject={formFieldsObject}
                defaultTemplateFields={defaultTemplateFields}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
